import { v4 as uuidv4 } from 'uuid';
import httpRequestMethodEnum from './enums/httpRequestMethod';
import fetchModeEnum from './enums/fetchMode';
import { apiEndpointEnum } from './enums/apiEndpoints';
import { apiHeadersEnum } from './enums/apiHeaders';
import { baseURLEnum } from './enums/baseURL';
import { baseURL, logger } from '../baselineApis';
import { baselineJWTExpiredWrapper, getBaselineJWT } from './libJWT';
import { wrappedTypeResponse } from './enums/wrappedTypeResponse';
import { imageAPI } from './imageAPI';

const convertToFormData = (formDataElementsObject) => {
  const formData = new FormData();
  Object.keys(formDataElementsObject).forEach((elementKey) =>
    formData.append(elementKey, formDataElementsObject[elementKey]),
  );

  return formData;
};

function createBearerHeader(authToken)
{
  return `Bearer ${authToken}`;
}

/**
 * Method: GET
 * @param  {number} reportId
 * @param  {string} image_info_version
 * @param  {string} preview_version
 */
async function _fetch(reportId, organization) {
  return getBaselineJWT().then((authToken) => {
    const url = baseURL(baseURLEnum.REPORT_ACCESS)+`/${apiEndpointEnum.REPORT}/${reportId}`;
    const headers = {
      authorization: createBearerHeader(authToken),
    };

    if (organization && organization.length) {
      headers[apiHeadersEnum.ORGANIZATION_ID] = organization;
    }


    logger.info({
      url: url,
      reportId: reportId,
      function: reportAPI?.fetch?.name,
      method: httpRequestMethodEnum.GET,
    });

    const request = new Request(url, {
      method: httpRequestMethodEnum.GET,
      headers:headers,
      mode:fetchModeEnum.CORS
    });

    return fetch(request);
  });
}

/**
 * Method: PUT
 * @param { string } reportId
 * @param  {report_info : File,
 * ...
 * fileN : File,
 * ...
 * } dataToStore
 */
async function _store(reportId, dataToStore, organization) {
  return getBaselineJWT().then((authToken)=> {

    const formData = convertToFormData(dataToStore);
    const url = baseURL(baseURLEnum.REPORT_ACCESS)+`/${apiEndpointEnum.REPORT}/${reportId}`;
    const headers = {
      authorization: createBearerHeader(authToken),
      accept: '*/*',
    };

    if (organization && organization.length) {
      headers[apiHeadersEnum.ORGANIZATION_ID] = organization;
    }

    const logHeaders = { ...headers };
    delete logHeaders.authorization;

    logger.info({
      url: url,
      reportId: reportId,
      function: reportAPI?.store?.name,
      method: httpRequestMethodEnum.PUT,
      data: formData,
      headers: logHeaders,
    });

    const request = new Request(url, {
      method: httpRequestMethodEnum.PUT,
      headers:headers,
      body: formData,
      mode:fetchModeEnum.CORS
    });

    return fetch(request);
  });
}

/**
 * Method: DELETE
 * @param  {number} reportId
 */
async function _deleteById(reportId, organization) {
  return getBaselineJWT().then((authToken) => {
    const url = baseURL(baseURLEnum.REPORT_ACCESS)+`/${apiEndpointEnum.REPORT}/${reportId}`;

    const headers = {
      authorization: createBearerHeader(authToken),
    };

    if (organization && organization.length) {
      headers[apiHeadersEnum.ORGANIZATION_ID] = organization;
    }

    logger.info({
      url: url,
      reportId: reportId,
      function: reportAPI?.deleteById?.name,
      method: httpRequestMethodEnum.DELETE,
    });

    const request = new Request(url, {
      method: httpRequestMethodEnum.DELETE,
      headers:headers,
      mode:fetchModeEnum.CORS
    });

    return fetch(request);
  });
}

/**
 * Method: GET
 * @param  {
 * search_string: string,
 * sort_field: string,
 * sort_direction: string,
 * search_id: string,
 * page_size: int,
 * page: int,
 * } searchValues
 */
async function _search(
  searchValues = {
    sort_field: '',
    sort_direction: '',
    search_id: uuidv4(),
    page_size: 20,
    page: 0,
  },
  organization
) {
  return getBaselineJWT().then((authToken)=>{
    const url = baseURL(baseURLEnum.REPORT_ACCESS)+`/${apiEndpointEnum.REPORT}/${apiEndpointEnum.SEARCH}`;
    const headers = {
      authorization: createBearerHeader(authToken),
    };

    if (organization && organization.length) {
      headers[apiHeadersEnum.ORGANIZATION_ID] = organization;
    }

    logger.info({
      url: url,
      params: searchValues,
      function: reportAPI?.search?.name,
      method: httpRequestMethodEnum.GET,
    });

    let newurl = new URL(url);
    for (let key in searchValues) { newurl.searchParams.append(key, searchValues[key]); }

    const request = new Request(newurl,{
      method: httpRequestMethodEnum.GET,
      headers:headers,
      mode:fetchModeEnum.CORS
    })

    return fetch(request);
  });
}


async function _fetchImageDataJSON(jsonURL) {
  const request = new Request(jsonURL, {
    method: httpRequestMethodEnum.GET,
  });
  return fetch(request).then((response)=>{return response.json() });
}

async function _fetchImage(irDataURL) {
  const request = new Request(irDataURL, {
    method: httpRequestMethodEnum.GET,
  });
  return fetch(request).then((response)=>{return response.blob() });
}

async function fetchAllReports(organization) {
  try {
    const response = await reportAPI.search({
        sort_field: '',
        sort_direction: '',
        search_id: uuidv4(),
        page_size: 20,
        page: 0,
      },
      organization);
    const {results:reports}  = response || {};

    const { finalized, draft } = reports.reduce(
      (acc, { data }) => {
        if (data?.finalized === true) {
          acc.finalized.push(data);
        } else {
          acc.draft.push(data);
        }
        return acc;
      },
      { finalized: [], draft: [] },
    );

    return { finalized, draft };
  } catch (error) {
    logger.error({ error, function: fetchAllReports.name });
  }
}

async function buildReportImagesArray(reportInfo, urls = {}, organization) {
  const { options, images } = reportInfo || {};
  const { finalized } = options || {};

  let imageURLs = [];

  if (finalized) {
    // finalized reports get images from report data presigned urls
    // build image archive object by id
    const imageArchives = Object.entries(urls).reduce(
      (result, [filename, url]) => {
        const is2uMatch = filename.match(/([\w-]+)_is2u/);
        const columbiaInfoMatch = filename.match(/columbia_info_([\w-]+)\.zip/)
        const imageId = is2uMatch?.[1] ?? columbiaInfoMatch?.[1] ?? null;
        result[imageId] = { id: imageId, url, version: null };
        return result;
      },
      {}
    );

    // sort the image archive purls
    imageURLs = await Promise.all(
      images.map((imageId) => imageArchives[imageId])
    );
  } else {
    // draft reports get images from report_info
    // sort the image archive purls
    if (!images.length) {
      return [];
    }
    const includeDeletedImages = true;
    const response = await imageAPI.fetchByMultipleIds(images, organization, includeDeletedImages);
    const s3Urls = response.map((image) => {
      return {
        name: image.sort_info.origin_path,
        url: image.s3_keys.is2u,
      };
    });
    return s3Urls;
  }
  // return array of archived image presigned urls
  return imageURLs;
}

async function fetchReportById(reportId,organization) {
  try {
    // fetch presigned urls (pURL) from report access api
    const [{ presigned_urls }] = await reportAPI.fetch(reportId,organization);

    // unpack pURLs
    // note wildcard columbia_info are served if draft is finalized
    const { report_info, company_logo, ...urls } = presigned_urls || {};

    // fetch report info by pURL
    const reportInfo = await reportAPI.fetchImageDataJSON(report_info);

    // pack array
    // if draft report, also fetch image urls by id
    const imageURLs = await buildReportImagesArray(
      reportInfo,
      urls,
      organization
    );

    // these illegal keys appended are later purged by reportAPI before each save
    reportInfo.company_logo = company_logo || '';
    reportInfo.image_urls = imageURLs || [];

    return reportInfo;
  } catch (error) {
    // Handle any errors
    logger.error({ error, function: fetchReportById.name });
    throw error;
  }
}

function filterObjectKeys(obj, includedKeys) {
  const filteredObject = {};

  Object.keys(obj).forEach((key) => {
    if (includedKeys.includes(key)) {
      filteredObject[key] = obj[key];
    }
  });

  return filteredObject;
}

function prepareJSONBlob (report_info) {
  const report = [JSON.stringify(report_info)];
  const blob = new Blob(report, { type: 'application/json' });
  return blob;
};

async function saveReportInfo(reportInfo) {
  const includedKeys = ['report_info', 'data', 'options', 'images', 'image_options'];

  try {
    // purge any illegal keys
    reportInfo = filterObjectKeys(reportInfo, includedKeys);

    // prepare as json blob
    const blob = prepareJSONBlob(reportInfo);

    // update by id
    const response = await reportAPI.store(reportInfo.options.id, { report_info: blob });

    return response;
  } catch (error) {
    logger.error({ error, function: saveReportInfo.name });
    return error;
  }
}

export const reportAPI = {
  fetch: baselineJWTExpiredWrapper(_fetch, wrappedTypeResponse.JSON),
  store: baselineJWTExpiredWrapper(_store, null),
  deleteById: baselineJWTExpiredWrapper(_deleteById, null),
  search: baselineJWTExpiredWrapper(_search, wrappedTypeResponse.JSON),
  fetchImageDataJSON: _fetchImageDataJSON,
  fetchImage: _fetchImage,
  fetchAllReports,
  fetchReportById,
  saveReportInfo
};
